import {
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/solid';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import type { EditorMode, PresetSummary } from './types';

interface PresetChipProps {
  presets: PresetSummary[];
  selectedPresetName: string | null;
  mode: EditorMode;
  onSelectPreset: (name: string) => void;
  onResetToPreset: () => void;
}

export function PresetChip({
  presets,
  selectedPresetName,
  mode,
  onSelectPreset,
  onResetToPreset,
}: PresetChipProps) {
  const active =
    presets.find(p => p.name === selectedPresetName) ?? presets[0] ?? null;
  const isModified = mode === 'modified';

  return (
    <div
      className={cn(
        'border-chariot-stroke flex items-center justify-between gap-3 rounded-md border-2 p-3',
        isModified && 'border-chariot-status-warning/60'
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-md',
            isModified
              ? 'bg-chariot-status-warning/15 text-chariot-status-warning'
              : 'bg-chariot-brand-primary/15 text-chariot-brand-primary'
          )}
        >
          <Squares2X2Icon className="size-5" />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="text-chariot-text-primary text-base font-semibold">
              {active?.display_name ?? 'No preset selected'}
            </span>
            {active?.cost_indicator && (
              <Badge
                variant="secondary"
                className="px-1.5 py-0 font-mono text-[10px] tracking-wider"
              >
                {active.cost_indicator}
              </Badge>
            )}
            {isModified && (
              <Badge
                variant="outline"
                className="border-chariot-status-warning text-chariot-status-warning px-2 py-0 text-[10px] font-semibold tracking-wider uppercase"
              >
                Modified
              </Badge>
            )}
          </div>
          {active?.summary && (
            <span className="text-chariot-text-secondary truncate text-xs">
              {active.summary}
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {isModified && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onResetToPreset}
            className="text-chariot-text-secondary hover:text-chariot-text-primary h-8 gap-1.5 text-xs"
            aria-label="Reset to preset"
          >
            <ArrowPathIcon className="size-3.5" />
            Reset
          </Button>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5"
              aria-label="Change pipeline preset"
            >
              Change preset
              <ChevronDownIcon className="text-chariot-text-secondary size-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-1">
            <div className="flex flex-col">
              {presets.map(preset => {
                const isSelected = preset.name === active?.name;
                return (
                  <button
                    type="button"
                    key={preset.name}
                    onClick={() => onSelectPreset(preset.name)}
                    className={cn(
                      'hover:bg-chariot-background-secondary flex flex-col items-start gap-1 rounded-sm px-3 py-2.5 text-left',
                      isSelected && 'bg-chariot-background-secondary'
                    )}
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className="text-chariot-text-primary text-sm font-semibold">
                        {preset.display_name}
                      </span>
                      <span className="flex items-center gap-1.5">
                        {preset.cost_indicator && (
                          <Badge
                            variant="secondary"
                            className="px-1.5 py-0 font-mono text-[10px]"
                          >
                            {preset.cost_indicator}
                          </Badge>
                        )}
                        {isSelected && (
                          <CheckIcon className="text-chariot-brand-primary size-4" />
                        )}
                      </span>
                    </div>
                    {preset.summary && (
                      <span className="text-chariot-text-secondary text-xs leading-relaxed">
                        {preset.summary}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
